import { useState, useEffect } from 'react';
import { Modal, Box, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faCheck, faComments, faGear, faCode, faX } from '@fortawesome/free-solid-svg-icons';
import './WebflowStepsModal.css';
import { useTranslation, Trans } from "react-i18next";
import '../translations.js';

const WebflowStepsModal = ({ open, onFinish }) => {
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      setStep(0);
      setFinished(false);
    }
  }, [open]);

  const steps = [
    <div className="step-content">
      <div className="step-header">{t('wf_steps_test_title')}</div>
      <ol className="instruction-list">
        <li>
          <Trans i18nKey="wf_steps_test_li1_a" components={{ 1: <strong /> }}>
            Go to the <strong>All Bots</strong> sidebar section and click on
          </Trans>
          <button className="steps-bot-btn">
            <FontAwesomeIcon icon={faComments} /> {t('test_bot')}
          </button>{" "}
          {t('wf_steps_test_li1_b')}
        </li>
        <li>
          {t('wf_steps_test_li2')}
        </li>
      </ol>
    </div>,

    <div className="step-content">
      <div className="step-header">{t('wf_steps_customize_title')}</div>
      <ol className="instruction-list">
        <li>
          {t('wf_steps_click_on')}
            <button className="steps-bot-btn">
            <FontAwesomeIcon icon={faGear} /> {t('settings')}
            </button>{" "}
            {t('wf_steps_of_your_chatbot')}
        </li>
        <li>
          <Trans i18nKey="wf_steps_customize_li2" components={{ 1: <strong />, 2: <strong />, 3: <strong /> }}>
            Fully customize the <strong>design</strong> of your assistant, add more <strong>knowledge</strong>, and define how it should <strong>behave</strong>.
          </Trans>
        </li>
      </ol>
    </div>,

    <div className="step-content">
      <div className="step-header">{t('wf_steps_integration_title')}</div>
      <ol className="instruction-list">
        <li>
          {t('wf_steps_click_on')}
            <button className="steps-bot-btn">
            <FontAwesomeIcon icon={faCode} /> {t('integration')}
            </button>{" "}
            {t('wf_steps_of_your_chatbot')}
        </li>
        <li>
          <Trans i18nKey="wf_steps_integration_li2" components={{ 1: <strong /> }}>
            Navigate to <strong>Webflow</strong> and manage the integration. Here you can activate & deactivate your assistant for your website.
          </Trans>
        </li>
      </ol>
    </div>,

    // <div className="step-content">
    //   <div className="step-header">Manage Subscription</div>
    //   <ol className="instruction-list">
    //     <li>
    //       Your chatbot includes <strong>20 free messages</strong> to help you get started.
    //     </li>
    //     <li>
    //       To unlock higher message limits, you can subscribe to a paid plan starting at just <strong>$20</strong> by clicking
    //       <button className='steps-plan-info-action-button'>
    //         <div className='steps-plan-info-action-button-content'>
    //           <FontAwesomeIcon icon={faCircleUp} />
    //           Upgrade
    //         </div>
    //       </button>
    //     </li>
    //   </ol>
    // </div>
  ];

  const handleFinish = () => {
    setFinished(true);
    onFinish();
  };

  return (
    <Modal open={open && !finished} onClose={() => {}} disableEscapeKeyDown>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2 : 4,
          width: isMobile ? '80%' : '90%',
          maxWidth: 600,
          maxHeight: '80vh',
          overflow: 'hidden',
          outline: 'none',
          paddingTop: '10px'
        }}
        ><div className="step-indicator">
            <div>
              {t('wf_steps_step_counter', { current: step + 1, total: steps.length })}
            </div>
            <button className="webflow-close-modal-button" onClick={handleFinish}>
              <FontAwesomeIcon icon={faX} />
            </button>
        </div>
        <div className="steps-wrapper">
          <div
            className="steps-slider"
            style={{
              transform: `translateX(-${step * 100}%)`
            }}
          >
            {steps.map((content, i) => (
              <div key={i} className="step-slide">
                {content}
              </div>
            ))}
          </div>
        </div>

        <div className="navigation-buttons">
          {step > 0 && (
            <Button
              variant="outlined"
              onClick={() => setStep((s) => s - 1)}
                sx={{
                    mr: 2,
                    borderColor: '#5616ea',
                    color: '#5616ea',
                    '&:hover': {
                    borderColor: '#4a14d6',
                    backgroundColor: "#f0f0f0"
                    },
                }}>
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '6px' }} />
              {t('live_inbox_back')}
            </Button>
          )}
          {step < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={() => setStep((s) => s + 1)}
              sx={{
                backgroundColor: '#5616ea',
                '&:hover': { backgroundColor: '#4a14d6' }
              }}
            >
              {t('next')} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '6px' }} />
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleFinish}
              sx={{
                backgroundColor: '#5616ea',
                '&:hover': { backgroundColor: '#4a14d6' }
              }}
            >
              <FontAwesomeIcon icon={faCheck} style={{ fontSize: '18px', marginRight: '6px' }} />
              {t('wf_steps_finish')}
            </Button>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default WebflowStepsModal;
